// Çağıranın şirket ekseni ve bağlam anahtarı (SEM-6041).
//
// Kendi sorgu anahtarında yaşıyor (`["me","company-axis"]`), `use-scope-tier`
// ile AYNI sebeple: /v1/me'yi kendi dilimine daraltan her hook cevabı o daraltma
// ile önbelleğe alır, anahtarı paylaşmak bu hook'a şirket alanı taşımayan bir
// yük verirdi.
//
// `authorized_legal_entity_ids` sunucuda `null` doğar ve bu "HOLDİNG GENELİ"
// demektir, "hiçbiri" değil (spec 01). Oluşturma formları bu ekseni şirket
// seçeneklerini daraltırken kullanır; nihai yazma yetkisini sunucu doğrular.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { platformGraph } from "@/api/platform-graph";

export interface CompanyOption {
  readonly legal_entity_id: string;
  readonly name: string;
}

export interface CompanyAxis {
  readonly companies: readonly CompanyOption[];
  readonly active_legal_entity_id: string | null;
  readonly authorized_legal_entity_ids?: readonly string[] | null;
}

export const COMPANY_AXIS_QUERY_KEY = ["me", "company-axis"] as const;

type MeGet = (path: "/v1/me") => Promise<EnvelopedFetchResult<CompanyAxis>>;

async function fetchCompanyAxis(): Promise<CompanyAxis> {
  const get = platformGraph.GET as unknown as MeGet;
  const data = await unwrapEnvelope(get("/v1/me"));
  return {
    companies: data.companies ?? [],
    active_legal_entity_id: data.active_legal_entity_id ?? null,
    authorized_legal_entity_ids: data.authorized_legal_entity_ids ?? null,
  };
}

/** Creation forms need to distinguish an empty company list from an unavailable one. */
export function useCompanyAxisQuery(): UseQueryResult<CompanyAxis> {
  return useQuery({
    queryKey: COMPANY_AXIS_QUERY_KEY,
    queryFn: fetchCompanyAxis,
    staleTime: 60_000,
  });
}

/** Eksen, veya /v1/me henüz dönmediyse boş liste (seçici hiç render edilmez). */
export function useCompanyAxis(): CompanyAxis {
  const query = useCompanyAxisQuery();
  return query.data ?? { companies: [], active_legal_entity_id: null };
}

type SetActiveCompany = (
  path: "/v1/me/active-company",
  init: { body: { legal_entity_id: string | null } },
) => Promise<EnvelopedFetchResult<{ active_legal_entity_id: string | null }>>;

/**
 * Bağlamı değiştirir, sorguları sıfırlar ve açık ekranları yeniden okur.
 *
 * Nokta atışı invalidate etmiyoruz çünkü hangi sorguların şirkete göre
 * süzüldüğü sunucu tarafında, yüzey yüzey büyüyen bir kümedir; istemcide o
 * kümenin bir kopyasını tutmak, yeni bir yüzey şirket kolonu ilan ettiği gün
 * sessizce bayatlardı — kullanıcı yeni şirketi seçer, ekran eski şirketin
 * satırlarını gösterir. `clear()` açık gözlemcileri eski sorguda bırakabildiği
 * için sorgular kendi gözlemcileriyle birlikte sıfırlanır; önceki istekler
 * iptal edilir, açık sorgular yeni şirket bağlamında yeniden çalışır.
 */
export function useSetActiveCompany(): (id: string | null) => void {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    scope: { id: "active-company" },
    mutationFn: async (id: string | null) => {
      const put = platformGraph.PUT as unknown as SetActiveCompany;
      return unwrapEnvelope(
        put("/v1/me/active-company", { body: { legal_entity_id: id } }),
      );
    },
    onSuccess: () => queryClient.resetQueries(),
  });
  return (id: string | null): void => {
    mutation.mutate(id);
  };
}
